import { useEffect } from "react";
import { useSelector } from "react-redux";
import { addReplaceHistory, replaceAll, setAllMatches, setCurrentMatchIndex, setReplaceText, setSearchQuery, updateBlock, updateText } from "../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { RootState } from "../redux/store";
import { useAppDispatch } from "../redux/useAppDispatch";
import { scrollIntoView } from "../utils/scrollIntoView";
import { TranscriptionBlockType } from "../utils/type";

export interface Match {
  textIndex: number;
  start: number;
  end: number;
}

const useFindReplace = (texts: TranscriptionBlockType[], type?: string, editorRef?: React.MutableRefObject<any>) => {
  // const [allMatches, setAllMatches] = useState<Match[]>([]);
  // const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const allMatches = useSelector((state: RootState) => state.qCDetailPageState.allMatches);
  const currentMatchIndex = useSelector((state: RootState) => state.qCDetailPageState.currentMatchIndex);
  const transcriptionId = useSelector((state: RootState) => state.qCDetailPageState.data.transcriptions[0].idTranscription);
  const replaceText = useSelector((state: RootState) => state.qCDetailPageState.replaceText);
  const searchQuery = useSelector((state: RootState) => state.qCDetailPageState.searchQuery);
  const isCCActive = useSelector((state: RootState) => state.qCDetailPageState.isCCActive);
  const isFullWordActive = useSelector((state: RootState) => state.qCDetailPageState.isFullWordActive);
  const dispatch = useAppDispatch();

  const setAllMatchesValue = (matches: Match[]) => {
    dispatch(setAllMatches(matches));
  };
  const setCurrentMatchIndexValue = (index: number) => {
    dispatch(setCurrentMatchIndex(index));
  };

  const updateTranscriptText = (newTexts: TranscriptionBlockType[]) => {
    dispatch(updateText(newTexts));
  };

  const findMatches = (query: string) => {
    //Adding the logic to search by normal search, full word and case sensitive

    let matches: Match[] = [];

    if (type === "Dictation") {
      const cleanDictation = editorRef?.current
        .getMarkdown()
        .replace(/<span class="highlighted">/g, "")
        .replace(/<\/span>/g, "");
      if (query === "") {
        setAllMatchesValue([]);
        setCurrentMatchIndexValue(0);
        return;
      }
      if (isCCActive && isFullWordActive) {
        const re = new RegExp(`\\b${query}\\b`, "g");
        let match;
        while ((match = re.exec(cleanDictation)) !== null) {
          matches.push({ textIndex: 0, start: match.index, end: match.index + query.length });
        }
      } else if (isCCActive) {
        const re = new RegExp(query, "g");
        let match;
        while ((match = re.exec(cleanDictation)) !== null) {
          matches.push({ textIndex: 0, start: match.index, end: match.index + query.length });
        }
      } else if (isFullWordActive) {
        const re = new RegExp(`\\b${query}\\b`, "gi");
        let match;
        while ((match = re.exec(cleanDictation)) !== null) {
          matches.push({ textIndex: 0, start: match.index, end: match.index + query.length });
        }
      } else {
        const re = new RegExp(query, "gi");
        let match;
        while ((match = re.exec(cleanDictation)) !== null) {
          matches.push({ textIndex: 0, start: match.index, end: match.index + query.length });
        }
      }
    } else {
      texts.forEach((text, textIndex) => {
        if (query === "") return;
        const transcript = text.transcript;
        if (isCCActive && isFullWordActive) {
          const re = new RegExp(`\\b${query}\\b`, "g");
          let match;
          while ((match = re.exec(transcript)) !== null) {
            matches.push({ textIndex, start: match.index, end: match.index + query.length });
          }
        } else if (isCCActive) {
          const re = new RegExp(query, "g");
          let match;
          while ((match = re.exec(transcript)) !== null) {
            matches.push({ textIndex, start: match.index, end: match.index + query.length });
          }
        } else if (isFullWordActive) {
          const re = new RegExp(`\\b${query}\\b`, "gi");
          let match;
          while ((match = re.exec(transcript)) !== null) {
            matches.push({ textIndex, start: match.index, end: match.index + query.length });
          }
        } else {
          const re = new RegExp(query, "gi");
          let match;
          while ((match = re.exec(transcript)) !== null) {
            matches.push({ textIndex, start: match.index, end: match.index + query.length });
          }
        }
      });
    }

    setAllMatchesValue(matches);
    setCurrentMatchIndexValue(0);
  };

  const handleNext = () => {
    const nextIndex = currentMatchIndex + 1 === allMatches.length ? 0 : currentMatchIndex + 1;

    scrollIntoView(allMatches[nextIndex].textIndex, false);
    setCurrentMatchIndexValue(nextIndex);
  };

  const handlePrev = () => {
    const prevIndex = currentMatchIndex - 1 === -1 ? allMatches.length - 1 : currentMatchIndex - 1;
    scrollIntoView(allMatches[prevIndex].textIndex, false);
    setCurrentMatchIndexValue(prevIndex);
  };

  const handleReplace = async () => {
    if (!allMatches.length) return;
    const { textIndex, start, end } = allMatches[currentMatchIndex];
    if (type === "Dictation") {
      dispatch(addReplaceHistory(replaceText));
      const cleanDictation = editorRef?.current
        .getMarkdown()
        .replace(/<span class="highlighted">/g, "")
        .replace(/<\/span>/g, "");

      const updatedText = cleanDictation.substring(0, start) + replaceText + cleanDictation.substring(end);
      await editorRef?.current.setMarkdown(updatedText);
      findMatches(searchQuery);
      return;
    }

    const updatedText = texts[textIndex].transcript.substring(0, start) + replaceText + texts[textIndex].transcript.substring(end);
    const newTexts = [...texts];
    newTexts[textIndex] = { ...newTexts[textIndex], transcript: updatedText };
    const payload = {
      TranscriptionId: transcriptionId,
      TranscriptionBlocks: [
        {
          TranscriptionBlockId: texts[textIndex].idTranscriptionBlock,
          StartTimeSec: texts[textIndex].startTimeSec,
          EndTimeSec: texts[textIndex].endTimeSec,
          Speaker: texts[textIndex].speaker,
          Text: updatedText,
        },
      ],
    };
    await dispatch(updateBlock({ id: transcriptionId, data: payload, blockIndex: textIndex }));
    updateTranscriptText(newTexts);
    dispatch(addReplaceHistory(replaceText));
    findMatches(searchQuery);
  };

  const handleReplaceAll = async () => {
    if (!searchQuery.trim() || !replaceText.trim()) return;
    if (type === "Dictation") {
      dispatch(addReplaceHistory(replaceText));
      let cleanDictation = editorRef?.current
        .getMarkdown()
        .replace(/<span class="highlighted">/g, "")
        .replace(/<\/span>/g, "");
      let updatedText;
      await allMatches.forEach((match: any) => {
        const { start, end } = match;
        updatedText = cleanDictation.substring(0, start) + replaceText + cleanDictation.substring(end);
        cleanDictation = updatedText;
      });
      await editorRef?.current.setMarkdown(updatedText);
      findMatches(searchQuery);
      return;
    }
    const payload = {
      TranscriptionId: transcriptionId,
      Text: searchQuery.trim(),
      Replacement: replaceText.trim(),
      WholeWords: isFullWordActive,
      CaseSensitive: isCCActive,
    };
    dispatch(replaceAll({ data: payload, id: transcriptionId }));
    dispatch(addReplaceHistory(replaceText));
  };

  useEffect(() => {
    findMatches(searchQuery);
  }, [texts, isCCActive, isFullWordActive]);

  return {
    searchQuery,
    replaceText,
    setSearchQuery,
    setReplaceText,
    texts,
    updateTranscriptText,
    allMatches,
    currentMatchIndex,
    handleNext,
    handlePrev,
    handleReplace,
    handleReplaceAll,
    findMatches,
  };
};

export default useFindReplace;
